import Dexie, { type EntityTable } from 'dexie'
import type { DevicePlatform, DevicePublicJwk } from '../api/types'

/**
 * The installation's device key (docs/api-contract.md "Devices").
 *
 * An ECDSA P-256 key pair whose private key is non-extractable, kept in its own small IndexedDB
 * database so signing out (which clears the chat database) never changes the device identity.
 * CryptoKey objects survive IndexedDB structured clone in every supported browser, Safari included.
 */

const KEY_ROW = 'device'
const ALGORITHM: EcKeyGenParams = { name: 'ECDSA', namedCurve: 'P-256' }
const SIGN_ALGORITHM: EcdsaParams = { name: 'ECDSA', hash: 'SHA-256' }

export const ATTEST_MESSAGE_PREFIX = 'boss-chat-device-attest.v1'

export interface DeviceKeyRow {
  name: string
  keyPair: CryptoKeyPair
  deviceId: string
  createdAt: string
}

export interface DeviceKey {
  keyPair: CryptoKeyPair
  deviceId: string
}

class DeviceKeyDatabase extends Dexie {
  keys!: EntityTable<DeviceKeyRow, 'name'>

  constructor(name = 'boss-chat-device') {
    super(name)
    this.version(1).stores({ keys: 'name' })
  }
}

export const deviceKeyDb = new DeviceKeyDatabase()

let cached: Promise<DeviceKey> | null = null
let persistRequested = false

function toHex(buffer: ArrayBuffer): string {
  return [...new Uint8Array(buffer)].map((b) => b.toString(16).padStart(2, '0')).join('')
}

export function base64UrlEncode(buffer: ArrayBuffer | Uint8Array): string {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
  let binary = ''
  for (const byte of bytes) binary += String.fromCharCode(byte)
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

export function base64UrlDecode(value: string): Uint8Array<ArrayBuffer> {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/')
  const raw = atob(normalized + '='.repeat((4 - (normalized.length % 4)) % 4))
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i)
  return bytes
}

/** Lower-case hex SHA-256 of the SPKI DER. */
export async function deviceIdFromSpki(spki: ArrayBuffer): Promise<string> {
  return toHex(await crypto.subtle.digest('SHA-256', spki))
}

/** Device id of a public key: lower-case hex SHA-256 of exportKey("spki"). */
export async function computeDeviceId(publicKey: CryptoKey): Promise<string> {
  return deviceIdFromSpki(await crypto.subtle.exportKey('spki', publicKey))
}

/** "A1B2-C3D4": the first 8 hex characters of the id, upper case, grouped. */
export function deviceCode(id: string): string {
  const head = id.slice(0, 8).toUpperCase()
  return `${head.slice(0, 4)}-${head.slice(4, 8)}`
}

/** The public key as the minimal JWK the server expects. */
export async function publicJwk(publicKey: CryptoKey): Promise<DevicePublicJwk> {
  const jwk = await crypto.subtle.exportKey('jwk', publicKey)
  if (jwk.kty !== 'EC' || jwk.crv !== 'P-256' || !jwk.x || !jwk.y) throw new Error('Unexpected device key format')
  return { kty: 'EC', crv: 'P-256', x: jwk.x, y: jwk.y }
}

/** The exact string the server verifies. */
export function attestationMessage(nonce: string, origin: string = location.origin): string {
  return `${ATTEST_MESSAGE_PREFIX}.${nonce}.${origin}`
}

/** Best effort: ask the browser not to evict storage (keeps the key on Safari). Runs once. */
function requestPersistence(): void {
  if (persistRequested) return
  persistRequested = true
  try {
    const storage = typeof navigator !== 'undefined' ? navigator.storage : undefined
    void storage?.persist?.().catch(() => undefined)
  } catch {
    // ignore
  }
}

async function generate(): Promise<DeviceKeyRow> {
  const keyPair = await crypto.subtle.generateKey(ALGORITHM, false, ['sign'])
  return {
    name: KEY_ROW,
    keyPair,
    deviceId: await computeDeviceId(keyPair.publicKey),
    createdAt: new Date().toISOString(),
  }
}

async function loadOrCreate(): Promise<DeviceKey> {
  requestPersistence()
  const existing = await deviceKeyDb.keys.get(KEY_ROW)
  if (existing?.keyPair?.privateKey && existing.deviceId) return { keyPair: existing.keyPair, deviceId: existing.deviceId }

  // WebCrypto promises cannot run inside an IndexedDB transaction, so generate first and only
  // write when no other tab stored a key in the meantime.
  const fresh = await generate()
  const row = await deviceKeyDb.transaction('rw', deviceKeyDb.keys, async () => {
    const raced = await deviceKeyDb.keys.get(KEY_ROW)
    if (raced?.keyPair?.privateKey && raced.deviceId) return raced
    await deviceKeyDb.keys.put(fresh)
    return fresh
  })
  return { keyPair: row.keyPair, deviceId: row.deviceId }
}

/** The installation's key pair, generated and stored on first use. */
export function getOrCreateDeviceKey(): Promise<DeviceKey> {
  if (!cached) {
    cached = loadOrCreate().catch((error: unknown) => {
      cached = null
      throw error
    })
  }
  return cached
}

/** This installation's device id. */
export async function deviceId(): Promise<string> {
  return (await getOrCreateDeviceKey()).deviceId
}

/** Sign a UTF-8 message; returns the raw 64-byte r||s signature as base64url without padding. */
export async function sign(message: string, privateKey?: CryptoKey): Promise<string> {
  const key = privateKey ?? (await getOrCreateDeviceKey()).keyPair.privateKey
  const signature = await crypto.subtle.sign(SIGN_ALGORITHM, key, new TextEncoder().encode(message))
  return base64UrlEncode(signature)
}

export function detectPlatform(): DevicePlatform | null {
  if (typeof navigator === 'undefined') return null
  const nav = navigator as Navigator & { userAgentData?: { platform?: string } }
  const ua = nav.userAgent ?? ''
  const hint = (nav.userAgentData?.platform ?? '').toLowerCase()
  if (/iPad|iPhone|iPod/.test(ua) || (nav.platform === 'MacIntel' && nav.maxTouchPoints > 1)) return 'ios'
  if (hint === 'android' || /Android/i.test(ua)) return 'android'
  if (hint === 'macos' || /Mac OS X|Macintosh/.test(ua)) return 'macos'
  if (hint === 'windows' || /Windows/.test(ua)) return 'windows'
  if (hint === 'linux' || hint === 'chrome os' || /Linux|CrOS/.test(ua)) return 'linux'
  return null
}

export function isInstalled(): boolean {
  if (typeof window === 'undefined') return false
  const nav = navigator as Navigator & { standalone?: boolean }
  return nav.standalone === true || window.matchMedia?.('(display-mode: standalone)').matches === true
}

/** Tests only: forget the in-memory key so the next call reads IndexedDB again. */
export function resetDeviceKeyCache(): void {
  cached = null
}
